import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled, keyframes } from '@mui/material/styles';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-12px); }
`;

const blink = keyframes`
  0%, 50%   { opacity: 1; }
  51%, 100% { opacity: 0; }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 4px 20px rgba(85,180,172,0.35), 0 0 0 1px rgba(85,180,172,0.15); }
  50%       { box-shadow: 0 4px 40px rgba(85,180,172,0.65), 0 0 0 1px rgba(85,180,172,0.35); }
`;

const StyledBox = styled('div')(({ theme }) => ({
  alignSelf: 'center',
  width: '100%',
  height: 400,
  marginTop: theme.spacing(8),
  borderRadius: 16,
  border: '1px solid rgba(85,180,172,0.25)',
  boxShadow: '0 0 0 1px rgba(85,180,172,0.08), 0 25px 70px rgba(0,0,0,0.55), 0 0 100px rgba(85,180,172,0.12)',
  backgroundImage: `url(${process.env.TEMPLATE_IMAGE_URL || 'https://mui.com'}/static/screenshots/material-ui/getting-started/templates/dashboard.jpg)`,
  backgroundSize: 'cover',
  animation: `${float} 6s ease-in-out infinite`,
  transition: 'all 0.35s ease',
  '&:hover': {
    transform: 'translateY(-6px) scale(1.01)',
    boxShadow: '0 0 0 1px rgba(85,180,172,0.3), 0 35px 90px rgba(0,0,0,0.65), 0 0 140px rgba(85,180,172,0.25)',
  },
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(10),
    height: 700,
  },
}));

export default function Hero() {
  const [displayedText, setDisplayedText] = React.useState('');
  const fullText = 'financiar';
  const [isTyping, setIsTyping] = React.useState(true);

  React.useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      if (i < fullText.length) {
        setDisplayedText(fullText.slice(0, i + 1));
        i++;
      } else {
        clearInterval(id);
        setIsTyping(false);
      }
    }, 100);
    return () => clearInterval(id);
  }, []);

  return (
    <Box
      id="hero"
      sx={{
        width: '100%',
        background: 'linear-gradient(140deg, #06111f 0%, #0b1f32 30%, #0d3547 65%, #186070 100%)',
        position: 'relative',
        overflow: 'hidden',
        /* radial glow spots */
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '15%',
          left: '8%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(85,180,172,0.1) 0%, transparent 65%)',
          borderRadius: '50%',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '5%',
          right: '8%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(24,88,107,0.18) 0%, transparent 65%)',
          borderRadius: '50%',
          pointerEvents: 'none',
        },
      }}
    >
      {/* Subtle grid mesh */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(85,180,172,0.035) 1px, transparent 1px),
            linear-gradient(90deg, rgba(85,180,172,0.035) 1px, transparent 1px)
          `,
          backgroundSize: '64px 64px',
          pointerEvents: 'none',
        }}
      />

      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 16, sm: 22 },
          pb: { xs: 14, sm: 18 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Pill badge */}
        <Box sx={{ animation: `${fadeInUp} 0.7s ease-out`, mb: 3 }}>
          <Chip
            label="✦ La plataforma financiera del futuro"
            sx={{
              background: 'linear-gradient(135deg, rgba(85,180,172,0.18), rgba(24,88,107,0.25))',
              border: '1px solid rgba(85,180,172,0.45)',
              color: '#82d8d4',
              fontWeight: 600,
              fontSize: '0.82rem',
              letterSpacing: '0.03em',
              px: 1,
              backdropFilter: 'blur(10px)',
            }}
          />
        </Box>

        <Stack
          spacing={2}
          useFlexGap
          sx={{ alignItems: 'center', width: { xs: '100%', sm: '80%' } }}
        >
          {/* Headline */}
          <Box
            sx={{
              textAlign: 'center',
              animation: `${fadeInUp} 0.85s ease-out 0.1s both`,
              mb: 2,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.6rem', sm: '3.8rem', md: '5rem', lg: '6rem' },
                fontWeight: 900,
                color: '#ffffff',
                letterSpacing: '-0.04em',
                lineHeight: 1.06,
                textShadow: '0 2px 20px rgba(0,0,0,0.4)',
              }}
            >
              La forma mas rapida de empezar a{' '}
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(90deg, #55b4ac 0%, #8ee0db 35%, #c5f5f3 55%, #55b4ac 80%, #8ee0db 100%)',
                  backgroundSize: '200% auto',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: `${shimmer} 3.5s linear infinite`,
                  filter: 'drop-shadow(0 0 18px rgba(85,180,172,0.55))',
                }}
              >
                {displayedText}
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: '3px',
                    height: '0.88em',
                    backgroundColor: '#55b4ac',
                    ml: '2px',
                    animation: isTyping ? 'none' : `${blink} 1s infinite`,
                    opacity: isTyping ? 1 : undefined,
                    verticalAlign: 'middle',
                    boxShadow: '0 0 8px rgba(85,180,172,0.9)',
                  }}
                />
              </Box>
              .
            </Typography>
          </Box>

          {/* Subtitle */}
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              color: 'rgba(255,255,255,0.58)',
              fontWeight: 400,
              width: { sm: '100%', md: '78%' },
              mx: 'auto',
              fontSize: { xs: '1rem', sm: '1.18rem' },
              lineHeight: 1.72,
              animation: `${fadeInUp} 0.85s ease-out 0.25s both`,
              mb: 4,
            }}
          >
            Automatiza préstamos, pagos, inventario y cobranzas desde una sola plataforma. Construida para escalar, diseñada para crecer.
          </Typography>

          {/* CTA buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            useFlexGap
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              animation: `${fadeInUp} 0.85s ease-out 0.4s both`,
            }}
          >
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 4.5,
                py: 1.8,
                minWidth: { xs: '100%', sm: '200px' },
                background: 'linear-gradient(135deg, #55b4ac 0%, #18586b 100%)',
                color: 'white',
                fontSize: '1rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2.5,
                animation: `${glowPulse} 3s ease-in-out infinite`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #65c4bc 0%, #226578 100%)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 10px 50px rgba(85,180,172,0.65)',
                },
              }}
            >
              Probar Demo →
            </Button>
            <Button
              variant="outlined"
              size="large"
              href="https://wa.me/18299665507?text=Hola!%20me%20interesa%20Bankivo"
              sx={{
                px: 4.5,
                py: 1.8,
                minWidth: { xs: '100%', sm: '200px' },
                borderColor: 'rgba(85,180,172,0.45)',
                color: 'rgba(255,255,255,0.82)',
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2.5,
                borderWidth: 1.5,
                background: 'rgba(255,255,255,0.04)',
                backdropFilter: 'blur(8px)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#55b4ac',
                  color: '#82d8d4',
                  backgroundColor: 'rgba(85,180,172,0.1)',
                  transform: 'translateY(-3px)',
                  borderWidth: 1.5,
                },
              }}
            >
              Agendar una llamada
            </Button>
          </Stack>

          {/* Micro trust line */}
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.3)',
              fontSize: '0.78rem',
              mt: 0.5,
              animation: `${fadeInUp} 0.85s ease-out 0.55s both`,
              letterSpacing: '0.03em',
            }}
          >
            Sin tarjeta de crédito · Cancela cuando quieras
          </Typography>
        </Stack>

        {/* Dashboard image + floating badges */}
        <Box
          sx={{
            mt: { xs: 8, sm: 12 },
            width: '100%',
            animation: `${fadeInUp} 1s ease-out 0.65s both`,
            position: 'relative',
          }}
        >
          {/* Badge — top left */}
          <Box
            sx={{
              position: 'absolute',
              top: { sm: 30 },
              left: { sm: -24 },
              zIndex: 2,
              background: 'rgba(255,255,255,0.07)',
              backdropFilter: 'blur(18px)',
              border: '1px solid rgba(85,180,172,0.28)',
              borderRadius: 2.5,
              px: 2.5,
              py: 1.5,
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              gap: 0.4,
              animation: `${float} 5.5s ease-in-out infinite`,
              boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
            }}
          >
            <Typography sx={{ color: '#55b4ac', fontWeight: 800, fontSize: '1.4rem', lineHeight: 1 }}>
              500+
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.7rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
              Empresas activas
            </Typography>
          </Box>

          {/* Badge — top right */}
          <Box
            sx={{
              position: 'absolute',
              top: { sm: 50 },
              right: { sm: -24 },
              zIndex: 2,
              background: 'rgba(255,255,255,0.07)',
              backdropFilter: 'blur(18px)',
              border: '1px solid rgba(85,180,172,0.28)',
              borderRadius: 2.5,
              px: 2.5,
              py: 1.5,
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              gap: 0.4,
              animation: `${float} 7s ease-in-out infinite reverse`,
              boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
            }}
          >
            <Typography sx={{ color: '#55b4ac', fontWeight: 800, fontSize: '1.4rem', lineHeight: 1 }}>
              $10M+
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.7rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
              Gestionados
            </Typography>
          </Box>

          {/* Badge — bottom right */}
          <Box
            sx={{
              position: 'absolute',
              bottom: { sm: 90 },
              right: { sm: -16 },
              zIndex: 2,
              background: 'rgba(255,255,255,0.07)',
              backdropFilter: 'blur(18px)',
              border: '1px solid rgba(85,180,172,0.28)',
              borderRadius: 2.5,
              px: 2,
              py: 1.25,
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              animation: `${float} 6s ease-in-out 1.2s infinite`,
              boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
            }}
          >
            <Box sx={{
              width: 9, height: 9, borderRadius: '50%',
              background: '#4caf50',
              boxShadow: '0 0 10px rgba(76,175,80,0.85)',
            }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
              99.9% Uptime
            </Typography>
          </Box>

          <StyledBox />
        </Box>
      </Container>

      {/* Wave transition to white */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          bottom: -1,
          left: 0,
          right: 0,
          lineHeight: 0,
          zIndex: 2,
        }}
      >
        <svg
          viewBox="0 0 1440 80"
          preserveAspectRatio="none"
          style={{ display: 'block', width: '100%', height: '80px' }}
        >
          <path
            d="M0,40 C240,80 480,0 720,40 C960,80 1200,10 1440,40 L1440,80 L0,80 Z"
            fill="white"
          />
        </svg>
      </Box>
    </Box>
  );
}
